// React imports
import { useState, useEffect, useRef, useMemo } from 'react';

// Generic imports
import _ from 'lodash';

// RTF Editor
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { MenuButtonBold, MenuButtonItalic, MenuControlsContainer, 
    MenuDivider, MenuSelectHeading, RichTextEditor } from 'mui-tiptap';

// MUI Imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

// FontAwesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPaperclip } from '@fortawesome/pro-light-svg-icons';

// Local imports
import TipTapMentionSuggestion, { mentionItemsFromOrg } from '../components/TipTapMentionSuggestion.js';
import ZPopper from '../components/ZPopper.js';

function TimelineEditor(props) {
    const { emails, organization, edit, initialUpdate, onEdit, onUpdateDone, onClose, possibleUpdateTags, featureToggle, noPadding, draftKey } = props;
    // Prefer rich items derived from organization (users + teams). Fall back to flat
    // emails list when callers haven't been migrated yet.
    const mentionItems = useMemo(
        () => organization ? mentionItemsFromOrg({ organization }) : (emails || []).map(e => ({ id: e, label: e, type: 'user' })),
        [organization, emails]
    );
    const [ tags, setTags ] = useState(initialUpdate?.editTags || initialUpdate?.tags || []);
    const [ selectedFiles, setSelectedFiles ] = useState(initialUpdate?.attachments || []);
    const rteRef = useRef(null);
    const fileInputRef = useRef(null);

    const savedDraft = draftKey ? sessionStorage.getItem(draftKey) : null;

    useEffect(() => {
        if (edit) {
            setTags(initialUpdate?.editTags || initialUpdate?.tags || []);
            setSelectedFiles(initialUpdate?.attachments || []);
        }
    }, [edit, initialUpdate]);

    useEffect(() => {
        if (savedDraft && !edit && !initialUpdate) {
            onEdit();
            const func = () => {
                if (rteRef.current?.editor) {
                    rteRef.current.editor.commands.setContent(savedDraft);
                } else {
                    setTimeout(func, 10);
                }
            };
            func();
        }
    }, []);

    if (!edit) {
        return (
            <TextField
                className={'nonotch'}
                fullWidth
                placeholder={'Do you have any updates to add?'}
                onMouseDown={() => {
                    onEdit();
                    const func = () => {
                        if (rteRef.current?.editor) {
                            rteRef.current?.editor?.chain().focus().run();
                        }
                        else {
                            setTimeout(func, 10)
                        }
                    };
                    func();
                }}
            />
        );
    }

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Box sx={{
            padding: noPadding ? '0px' : '24px 0px',
            '& .MuiTiptap-MenuBar-root': {
                background: 'transparent !important',
            }
        }}>
            <RichTextEditor
                ref={rteRef}
                extensions={[
                    StarterKit,
                    Mention.configure({
                        suggestion: TipTapMentionSuggestion({ items: mentionItems }),
                    }),
                ]}
                content={savedDraft || initialUpdate?.editHtml || initialUpdate?.html || ''}
                onUpdate={({ editor }) => {
                    if (draftKey) {
                        const html = editor.getHTML();
                        if (html && html.length > 7) {
                            sessionStorage.setItem(draftKey, html);
                        } else {
                            sessionStorage.removeItem(draftKey);
                        }
                    }
                }}
                renderControls={() => (
                    <MenuControlsContainer>
                    <MenuSelectHeading />
                    <MenuDivider />
                    <MenuButtonBold />
                    <MenuButtonItalic />
                    </MenuControlsContainer>
                )}
            />

            {!_.isNull(possibleUpdateTags) && (
                <Autocomplete
                    clearIcon={false}
                    options={_.xorBy(possibleUpdateTags, tags, a => a.label?.toLowerCase())}
                    filterOptions={(options, state) => {
                        const { inputValue } = state;
                        if (inputValue.length < 1) return [];
                        return _.sortBy(options.filter(a => a.label?.toLowerCase().includes(inputValue.toLowerCase())), a => a.label);
                    }}
                    freeSolo
                    multiple
                    renderTags={(value, props) =>
                        value.map((option, index) => (
                            <Chip label={option.label} {...props({ index })} />
                        ))
                    }
                    value={tags || []}
                    onChange={(e, value) => {
                        const _tags = value.map(a => {
                            if (typeof a === 'string') {
                                const existing = possibleUpdateTags.find(t => t.label?.toLowerCase() === a.toLowerCase());
                                return existing || { label: a };
                            }
                            return a;
                        });
                        setTags(_tags);
                    }}
                    renderInput={(params) => <TextField label='Tags' {...params} />}
                    sx={{ mt: 2 }}
                    slots={{
                        popper: ZPopper
                    }}
                    PopperComponent={ZPopper}
                    MenuProps={{
                        style: {
                            zIndex: 1000000,
                        },
                    }}
                />
            )}

            <input 
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                multiple
            />

            {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    {selectedFiles.map((file, index) => (
                        <Chip
                            key={index}
                            label={file.name}
                            onDelete={() => removeFile(index)}
                            sx={{ mr: 1, mb: 1 }}
                        />
                    ))}
                </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                <Box sx={{ display: 'flex', flex: 1, gap: 3 }}>
                    <Button
                        sx={{ flex: 1 }}
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            const html = rteRef.current?.editor?.getHTML() || '';
                            if (html.length > 10 || !_.isEmpty(selectedFiles) || !_.isEmpty(tags)) {
                                onUpdateDone({ html: html.length > 10 ? html : '<p></p>', tags, files: selectedFiles });
                            }
                            if (draftKey) sessionStorage.removeItem(draftKey);
                            onClose();
                        }}
                    >
                        {initialUpdate?.editHtml || initialUpdate?.html ? 'Update' : 'Post'}
                    </Button>
                    <IconButton
                        color='primary'
                        sx={{ mr: '12px !important' }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <FontAwesomeIcon icon={faPaperclip} />
                    </IconButton>
                </Box>
                {featureToggle['feature.account-email'] && (
                    <Button sx={{ flex: 0, minWidth: '200px', marginLeft: '6px' }} variant='contained' color='secondary'>
                        <FontAwesomeIcon icon={faPaperPlane} />&nbsp;Send Email
                    </Button>
                )}
            </Box>
        </Box>
    );
}

export default TimelineEditor;